// A meta repo's repos.yaml settings (P-007, every meta-kit 1.8.0 key since P-021): a form grouped
// as Code repo (one tab per code repo), Agents, Handoffs, Review and merge, Automation, Bugs, Hosts
// and About, each setting with an (i) tooltip from the catalogue (lib/settings.ts); and Raw YAML
// with the searchable field guide. The form sends only changed keys (`changes`; null removes one),
// which the API applies in place so the file's comments stay; Raw YAML sends the whole text. Keys a
// project adds itself are listed as project keys and edited in Raw YAML.
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { Shell } from '../components/Shell';
import { AddName, CommandList, PairList, SettingRow, SettingTip, Toggle } from '../components/SettingsFields';
import { Badge, Button, Input, Select, TabPanel, Tabs, TagInput, Textarea, cn, mono } from '../components/ui';
import { GROUPS, KNOWN_REPO, KNOWN_TOP, PROJECT_KEY, REPO_SETTINGS, setting, type GroupId, type Setting } from '../lib/settings';
import { findWorkspace, useSession } from '../lib/session';
import { useLoad } from '../lib/useLoad';
import { NotFoundPage } from './NotFoundPage';

type Values = Record<string, unknown>;
function mapping(value: unknown): Values {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Values) : {};
}
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);

/** A change's key: plain dotted for top-level settings and labels, `::`-joined and URI-encoded for
 *  anything under a name (repos, hosts), which may hold dots. */
function key(parts: string[]): string {
  return parts[0] === 'repos' || parts[0] === 'hosts' ? parts.map(encodeURIComponent).join('::') : parts.join('.');
}
function parts(k: string): string[] {
  return k.includes('::') ? k.split('::').map(decodeURIComponent) : k.split('.');
}
function hostPath(host: string, field?: string, repo?: string) {
  return key(['hosts', host, ...(field ? [field] : []), ...(repo ? [repo] : [])]);
}
/** The catalogue's path for a concrete one: names (which may hold dots) become placeholders. */
function catalogPath(p: string[]): string {
  const out = [...p];
  if ((out[0] === 'repos' || out[0] === 'hosts') && out.length > 1) out[1] = out[0] === 'repos' ? '<key>' : '<name>';
  if (out[0] === 'repos' && ['components', 'sequences', 'truth_docs'].includes(out[2] ?? '') && out.length > 3) out[3] = '<name>';
  return out.join('.');
}
const SECTION = 'scroll-mt-3 [&+section]:mt-[26px]';
const TABLE = 'rounded-lg border border-hairline bg-page';
const SUB_CARD = 'grid w-full gap-2 rounded border border-hairline bg-surface p-2.5';
const KV = 'grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5 text-[12px]';

export function SettingsEditor({ repo, onSaved }: { repo: number; onSaved?: () => void }) {
  const [loaded, reload] = useLoad(() => api.GET('/api/repos/{repo_id}/settings', { params: { path: { repo_id: repo } } }), String(repo));
  const [raw, setRaw] = useState(false);
  const [yaml, setYaml] = useState<string | null>(null);
  const [changes, setChanges] = useState<Values>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');
  const [newHost, setNewHost] = useState('');
  const [tab, setTab] = useState<string | null>(null);
  // Bumped on save and reload so list editors start again from the file.
  const [generation, setGeneration] = useState(0);
  if (loaded.state === 'loading') return <p>Loading repos.yaml…</p>;
  if (loaded.state === 'error') return <p role="alert">{loaded.message}</p>;
  const baseline = loaded.data;
  const values = mapping(baseline.values);
  const format = Number(values.format ?? 1);
  const shown = (s: Setting) => !(format === 1 && s.format === 2);

  function original(path: string[]): unknown {
    return path.reduce<unknown>((c, k) => mapping(c)[k], values);
  }
  function value(path: string[]): unknown {
    const k = key(path);
    return k in changes ? changes[k] : original(path);
  }
  function change(path: string[], next: unknown) {
    const k = key(path);
    setMessage('');
    setChanges((old) => {
      const out = { ...old };
      // Removing an entry drops the edits under it; back to the file's value is no change.
      if (next === null) for (const c of Object.keys(out)) if (c.startsWith(`${k}::`)) delete out[c];
      const before = original(path);
      const fallback = before === undefined && typeof next === 'boolean' && setting(catalogPath(path)).default === (next ? 'on' : 'off');
      if (JSON.stringify(next) === JSON.stringify(before) || (next === null && before === undefined) || fallback) delete out[k];
      else out[k] = next;
      return out;
    });
  }
  /** The names under a mapping: the file's, and those the form added, less those it removed. */
  function names(path: string[]): string[] {
    const prefix = `${key(path)}::`;
    const added = Object.keys(changes)
      .filter((c) => c.startsWith(prefix))
      .map((c) => decodeURIComponent(c.slice(prefix.length).split('::')[0]!));
    return Array.from(new Set([...Object.keys(mapping(original(path))), ...added])).filter((n) => changes[key([...path, n])] !== null);
  }
  function resetChanges() {
    setChanges({});
    setYaml(null);
    setGeneration((g) => g + 1);
  }
  async function save() {
    setBusy(true);
    setMessage('');
    const { data, error, response } = await api.POST('/api/repos/{repo_id}/settings', {
      params: { path: { repo_id: repo } },
      body: { sha: baseline.sha, branch: baseline.branch, ...(raw ? { yaml: yaml ?? baseline.yaml } : { changes }) },
    });
    setBusy(false);
    if (data) {
      setMessage('Saved as one commit.');
      resetChanges();
      reload();
      onSaved?.();
    } else setMessage(errorMessage(error, response.status));
  }

  // ---- controls, by kind; each names itself by its label (and code repo, where there are several)
  const yamlKey = (path: string[]) => path.join('.');
  const reset = (path: string[]) =>
    path[0] !== 'format' && original(path) !== undefined && setting(catalogPath(path)).default !== undefined ? () => change(path, null) : undefined;
  function text(path: string[], label: string, placeholder?: string) {
    return <Input aria-label={label} className="h-[30px]" value={String(value(path) ?? '')} placeholder={placeholder ?? setting(catalogPath(path)).default} onChange={(e) => change(path, e.target.value === '' ? null : e.target.value)} />;
  }
  function number(path: string[], label: string) {
    const v = value(path);
    return <Input aria-label={label} type="number" min={1} className="h-[30px] w-[90px]" value={v === null || v === undefined ? '' : String(v)} placeholder={setting(catalogPath(path)).default} onChange={(e) => change(path, e.target.value === '' ? null : Number(e.target.value))} />;
  }
  function select(path: string[], label: string, options: string[]) {
    const v = value(path);
    const current = v === false ? 'off' : v === null || v === undefined ? '' : String(v);
    const fallback = setting(catalogPath(path)).default;
    return (
      <Select aria-label={label} value={current} onChange={(e) => change(path, e.target.value === '' ? null : e.target.value)}>
        <option value="">Default{fallback ? ` (${fallback})` : ''}</option>
        {Array.from(new Set([...options, current].filter(Boolean))).map((o) => (
          <option key={o}>{o}</option>
        ))}
      </Select>
    );
  }
  function toggle(path: string[], label: string) {
    return <Toggle label={label} value={value(path)} fallback={setting(catalogPath(path)).default === 'on'} onChange={(on) => change(path, on)} />;
  }
  function tags(path: string[], label: string) {
    const v = value(path);
    const fallback = setting(catalogPath(path)).default;
    return (
      <>
        <TagInput label={label} value={strings(v)} placeholder={Array.isArray(v) ? 'none' : '+ add'} onChange={(next) => change(path, next)} />
        {!Array.isArray(v) && <span className="text-[12px] text-ink-muted">default: {fallback ?? 'none'}</span>}
      </>
    );
  }
  function commands(path: string[], label: string) {
    return <CommandList key={`${generation}-${key(path)}`} label={label} value={strings(value(path))} placeholder={`None${original(path) === undefined ? ' · default' : ''}`} onChange={(next) => change(path, next)} />;
  }
  function row(path: string[], control: ReactNode, label?: string) {
    const s = setting(catalogPath(path));
    if (!shown(s)) return null;
    return (
      <SettingRow key={key(path)} path={catalogPath(path)} yamlKey={yamlKey(path)} label={label} reset={reset(path)}>
        {control}
      </SettingRow>
    );
  }
  function projectRow(path: string[], v: unknown) {
    const sub = mapping(v);
    return (
      <SettingRow key={key(path)} path={PROJECT_KEY.path} yamlKey={yamlKey(path)} label={path[path.length - 1]} extra={<Badge tone="warning">Project key</Badge>}>
        <span className="text-[12px] text-ink-muted">
          {Object.keys(sub).length ? `${Object.keys(sub).join(', ')} · ` : ''}edit in Raw YAML
        </span>
      </SettingRow>
    );
  }

  // ---- the code repo group: one tab per repos key, and a tab to add one
  const repoKeys = names(['repos']);
  const current = tab && repoKeys.includes(tab) ? tab : repoKeys[0] ?? null;
  function codeRepo(k: string) {
    const r = ['repos', k];
    const at = (...rest: string[]) => [...r, ...rest];
    const label = (name: string) => `${k} · ${name}`;
    const proof = ['command', 'list_command', 'scenarios', 'output'];
    const components = names(at('components'));
    const sequences = names(at('sequences'));
    const extras = Object.entries(mapping(original(r))).filter(([name]) => !KNOWN_REPO.has(name));
    return (
      <div className={TABLE} key={`${generation}-${k}`}>
        {row(at('remote'), text(at('remote'), label('remote'), 'github.com/owner/name'))}
        {row(at('base_branch'), text(at('base_branch'), label('base branch'), 'staging'))}
        {row(at('default_branch'), text(at('default_branch'), label('release branch'), 'main'))}
        {row(at('path_hint'), text(at('path_hint'), label('local clone'), `~/orca/${k}`))}
        {row(at('agent_guide'), text(at('agent_guide'), label('agent guide')))}
        {row(at('test_commands'), commands(at('test_commands'), label('test commands')))}
        {row(at('lint_commands'), commands(at('lint_commands'), label('lint commands')))}
        {row(at('setup_commands'), commands(at('setup_commands'), label('setup commands')))}
        {row(at('ship_through'), select(at('ship_through'), label('ship through'), ['off', 'merge', 'deploy']))}
        {row(at('deploy_commands'), commands(at('deploy_commands'), label('deploy commands')))}
        {row(at('merge_flags'), tags(at('merge_flags'), label('merge flags')))}
        {row(
          at('sequences'),
          <div className="grid w-full gap-1.5">
            {sequences.map((name) => (
              <div key={name} className={SUB_CARD}>
                <div className="flex items-center gap-1.5">
                  <strong className={cn(mono, 'text-[12px]')}>{name}</strong>
                  <Button size="sm" variant="ghost" className="ml-auto" aria-label={`Remove ${name}`} onClick={() => change(at('sequences', name), null)}>
                    Remove
                  </Button>
                </div>
                <div className={KV}>
                  {(['dir', 'pattern'] as const).map((f) => (
                    <SubField key={f} path={`repos.<key>.sequences.<name>.${f}`} yamlKey={yamlKey(at('sequences', name, f))}>
                      {text(at('sequences', name, f), `${name} · ${setting(`repos.<key>.sequences.<name>.${f}`).label.toLowerCase()}`, f === 'pattern' ? 'NNNN_' : 'db/migrations')}
                    </SubField>
                  ))}
                </div>
              </div>
            ))}
            <AddName label="numbered files" taken={sequences} onAdd={(name) => change(at('sequences', name, 'dir'), '')} />
          </div>,
        )}
        {row(
          at('proof'),
          <div className={cn(SUB_CARD, KV)}>
            {proof.map((f) => (
              <SubField key={f} path={`repos.<key>.proof.${f}`} yamlKey={yamlKey(at('proof', f))}>
                {text(at('proof', f), label(`proof ${f.replace('_', ' ')}`))}
              </SubField>
            ))}
          </div>,
        )}
        {row(
          at('truth_docs'),
          <PairList
            label={label('truth docs')}
            names={names(at('truth_docs'))}
            value={(name) => String(value(at('truth_docs', name)) ?? '')}
            onChange={(name, v) => change(at('truth_docs', name), v)}
            onRemove={(name) => change(at('truth_docs', name), null)}
            onAdd={(name) => change(at('truth_docs', name), '')}
          />,
        )}
        {row(at('standards'), text(at('standards'), label('coding standards'), 'docs/standards.md'))}
        {row(
          at('components'),
          <div className="grid w-full gap-1.5">
            {components.map((name) => {
              const c = (...rest: string[]) => at('components', name, ...rest);
              const sub = (f: string) => `repos.<key>.components.<name>.${f}`;
              return (
                <div key={name} className={SUB_CARD}>
                  <div className="flex items-center gap-1.5">
                    <strong className={cn(mono, 'text-[12px]')}>{name}</strong>
                    <Button size="sm" variant="ghost" className="ml-auto" aria-label={`Remove ${name}`} onClick={() => change(c(), null)}>
                      Remove
                    </Button>
                  </div>
                  <div className={KV}>
                    <SubField path={sub('path')} yamlKey={yamlKey(c('path'))}>
                      {text(c('path'), `${name} · folder`, `${name}/`)}
                    </SubField>
                    <SubField path={sub('guide')} yamlKey={yamlKey(c('guide'))}>
                      {text(c('guide'), `${name} · guide`, 'none')}
                    </SubField>
                    {shown(setting(sub('test_commands'))) && (
                      <SubField path={sub('test_commands')} yamlKey={yamlKey(c('test_commands'))}>
                        {commands(c('test_commands'), `${name} · tests`)}
                      </SubField>
                    )}
                    <SubField path={sub('lint_commands')} yamlKey={yamlKey(c('lint_commands'))}>
                      {commands(c('lint_commands'), `${name} · lint`)}
                    </SubField>
                    <SubField path={sub('needs')} yamlKey={yamlKey(c('needs'))}>
                      {tags(c('needs'), `${name} needs`)}
                    </SubField>
                  </div>
                </div>
              );
            })}
            <AddName label="component" taken={components} onAdd={(name) => change(at('components', name, 'path'), name)} />
          </div>,
        )}
        {row(at('runner', 'orca_repo'), text(at('runner', 'orca_repo'), label('Orca repo selector'), 'id:… · name:… · path:…'))}
        {extras.map(([name, v]) => projectRow(at(name), v))}
      </div>
    );
  }

  // ---- hosts, as P-007 had them
  const hostMap = mapping(values.hosts);
  const repoMap = mapping(values.repos);
  const editedHosts = Object.keys(changes)
    .filter((k) => k.startsWith('hosts::') && k.split('::').length === 3)
    .map((k) => decodeURIComponent(k.split('::')[1]!));
  const hostNames = Array.from(new Set([...Object.keys(hostMap), ...editedHosts])).filter((name) => changes[hostPath(name)] !== null);
  const selectorInput = (path: string, label: string, fallback?: string) => (
    <Input key={path} aria-label={label} className="h-[30px]" placeholder="id:… · name:… · path:…" value={String(changes[path] ?? parts(path).reduce<unknown>((c, k) => mapping(c)[k], values) ?? fallback ?? '')} onChange={(e) => setChanges((old) => ({ ...old, [path]: e.target.value }))} />
  );
  const hosts = (
    <div className={TABLE} data-setting="hosts">
      {hostNames.map((name) => {
        const entry = hostMap[name] ?? {};
        const selector = mapping(entry).orca_repo;
        const defaultPath = hostPath(name, 'orca_repo');
        const stagedDefault = changes[defaultPath];
        const slots = hostPath(name, 'slots') in changes ? changes[hostPath(name, 'slots')] : mapping(entry).slots;
        return (
          <SettingRow key={name} path="hosts.<name>" yamlKey={`hosts.${name}`} label={name}>
            <div className={SUB_CARD}>
              <div className={KV}>
                <SubField path="hosts.<name>.slots" yamlKey={`hosts.${name}.slots`}>
                  <Input aria-label={`${name} slot ceiling`} type="number" min={1} className="h-[30px] w-[90px]" placeholder="from machines file" value={slots === null || slots === undefined ? '' : String(slots)} onChange={(e) => setChanges((old) => ({ ...old, [hostPath(name, 'slots')]: e.target.value === '' ? null : Number(e.target.value) }))} />
                </SubField>
                {Object.keys(repoMap).length === 0 ? (
                  <SubField path="hosts.<name>.orca_repo" yamlKey={`hosts.${name}.orca_repo`}>
                    {selectorInput(defaultPath, `${name} default Orca repo selector`)}
                  </SubField>
                ) : (
                  Object.keys(repoMap).map((k) => (
                    <SubField key={k} path="hosts.<name>.orca_repo.<repo key>" yamlKey={`hosts.${name}.orca_repo.${k}`} label={`Orca repo · ${k}`}>
                      {selectorInput(hostPath(name, 'orca_repo', k), `${name} · ${k} selector`, typeof selector === 'string' ? selector : typeof stagedDefault === 'string' ? stagedDefault : undefined)}
                    </SubField>
                  ))
                )}
              </div>
              {(typeof selector === 'string' || typeof stagedDefault === 'string') && Object.keys(repoMap).length > 0 && (
                <p className="m-0 text-[12px] text-ink-muted">Its one selector is used for each code repo until you set one here.</p>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setChanges((old) => ({ ...old, [hostPath(name)]: null }))}>
              Remove environment
            </Button>
          </SettingRow>
        );
      })}
      <div className="flex flex-wrap items-center gap-1.5 px-3.5 py-[11px]">
        <Input aria-label="New exact Orca environment name" className="h-[30px] max-w-[260px]" value={newHost} onChange={(e) => setNewHost(e.target.value)} placeholder="Exact saved environment name" />
        <Button
          size="sm"
          onClick={() => {
            if (newHost.trim()) {
              setChanges((old) => ({ ...old, [hostPath(newHost.trim(), 'orca_repo')]: '' }));
              setNewHost('');
            }
          }}
        >
          + Add Orca environment
        </Button>
      </div>
    </div>
  );

  const roles = Array.from(new Set(['bug', 'needs_triage', 'agent_ready', 'in_progress', 'hold', 'merged', 'duplicate', ...Object.keys(mapping(values.labels))]));
  const projectKeys = Object.entries(values).filter(([name]) => !KNOWN_TOP.has(name));
  const top = (path: string, control: ReactNode) => row([path], control);
  const sections: Record<GroupId, ReactNode> = {
    code: (
      <>
        <Tabs
          className="mb-2.5"
          label="Code repo"
          idPrefix="code-repo"
          tabs={repoKeys.map((k) => ({ id: k, label: k }))}
          value={current ?? ''}
          onValueChange={setTab}
        />
        {current ? (
          <TabPanel id={current} idPrefix="code-repo">
            {codeRepo(current)}
          </TabPanel>
        ) : (
          <p className="m-0 text-ink-muted">This meta repo names no code repo yet.</p>
        )}
        <div className="mt-2.5">
          <AddName
            label="code repo"
            taken={repoKeys}
            placeholder="short key, such as app"
            onAdd={(k) => {
              change(['repos', k, 'remote'], '');
              setTab(k);
            }}
          />
        </div>
        {current && (
          <Button size="sm" variant="ghost" className="mt-1.5" onClick={() => change(['repos', current], null)}>
            Remove code repo {current}
          </Button>
        )}
      </>
    ),
    agents: (
      <div className={TABLE}>
        {top('runner', select(['runner'], 'Runner', ['orca-supervised', 'orca', 'manual']))}
        {top(
          'agent',
          <>
            <Input aria-label="Agent" list="settings-agents" className="h-[30px] max-w-[220px]" value={String(value(['agent']) ?? '')} placeholder="claude" onChange={(e) => change(['agent'], e.target.value === '' ? null : e.target.value)} />
            <datalist id="settings-agents">
              {['claude', 'codex', 'amp'].map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
          </>,
        )}
        {top('fallback_agents', tags(['fallback_agents'], 'Fallback agents'))}
      </div>
    ),
    handoffs: (
      <div className={TABLE}>
        {top('in_flight_cap', number(['in_flight_cap'], 'In-flight cap'))}
        {top('placement', select(['placement'], 'Placement', ['local-first', 'remote-first', 'least-loaded']))}
        {top('auto_handoff', toggle(['auto_handoff'], 'Start handoffs automatically'))}
      </div>
    ),
    merge: (
      <div className={TABLE}>
        {top('merge_method', select(['merge_method'], 'Merge method', ['merge', 'squash', 'rebase']))}
        {top('auto_merge_base', toggle(['auto_merge_base'], 'Merge clean PRs automatically'))}
        {top('require_up_to_date', toggle(['require_up_to_date'], 'Require PRs up to date'))}
        {top('auto_send_back', toggle(['auto_send_back'], 'Send short work back automatically'))}
        {top('max_rounds', number(['max_rounds'], 'Most rounds per item'))}
        {top('delete_merged_branches', toggle(['delete_merged_branches'], 'Clean up after merge'))}
      </div>
    ),
    auto: (
      <div className={TABLE}>
        {top('auto_triage', toggle(['auto_triage'], 'Triage straightforward bugs'))}
        {top('auto_lock', toggle(['auto_lock'], 'Lock plans automatically'))}
        {top('auto_commit', toggle(['auto_commit'], 'Commit planning updates'))}
      </div>
    ),
    bugs: (
      <div className={TABLE}>
        {top('issues_repo', select(['issues_repo'], 'Issues come from', repoKeys))}
        {top(
          'labels',
          <div className="grid w-full gap-1.5">
            {roles.map((role) => {
              const s = setting(`labels.${role}`);
              return (
                <div key={role} className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-1.5">
                  <span className="flex items-center gap-1">
                    <code className={cn(mono, 'text-ink-display')}>{role}</code>
                    <SettingTip s={s} yamlKey={`labels.${role}`} label={`label ${role}`} />
                  </span>
                  <Input aria-label={`Label · ${role}`} className="h-[30px]" value={String(value(['labels', role]) ?? '')} placeholder={s.default} onChange={(e) => change(['labels', role], e.target.value === '' ? null : e.target.value)} />
                </div>
              );
            })}
          </div>,
        )}
      </div>
    ),
    hosts,
    about: (
      <div className={TABLE}>
        {top('product', text(['product'], 'Product'))}
        {top(
          'format',
          <>
            <code className={mono}>{format}</code>
            <span className="text-[12px] text-ink-muted">read only</span>
          </>,
        )}
        {top('meta_path_hint', text(['meta_path_hint'], 'Meta repo clone', '~/orca/<product>-meta'))}
        {top('stack_note', text(['stack_note'], 'Cost of a worktree'))}
        {top('verify', toggle(['verify'], 'Paths are hints'))}
        {projectKeys.map(([name, v]) => projectRow([name], v))}
      </div>
    ),
  };
  const guide = REPO_SETTINGS.filter(shown)
    .concat(PROJECT_KEY)
    .filter((f) => `${f.path} ${f.label} ${f.description}`.toLowerCase().includes(query.toLowerCase()));
  const dirty = raw ? (yaml ?? baseline.yaml) !== baseline.yaml : Object.keys(changes).length > 0;

  return (
    <section className="grid gap-4" aria-label="Repository settings editor">
      <p className="m-0 text-ink-muted">
        <code className={mono}>repos.yaml</code> on <code className={mono}>{baseline.branch}</code>. Saves commit directly as your linked GitHub account.
      </p>
      {baseline.reason && <p role="alert">{baseline.reason}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-hairline pb-3">
        <Tabs
          label="Editor mode"
          idPrefix="settings-mode"
          tabs={[
            { id: 'form', label: 'Form' },
            { id: 'raw', label: 'Raw YAML' },
          ]}
          value={raw ? 'raw' : 'form'}
          onValueChange={(id) => setRaw(id === 'raw')}
        />
        <span className="flex items-center gap-1.5 text-[12px] text-ink-muted">
          <span aria-hidden="true" className="inline-grid size-[18px] place-items-center rounded-full border border-hairline-strong text-[11px] font-semibold">
            i
          </span>
          hover, focus or tap for what a setting does
        </span>
      </div>
      <p className="m-0 text-[12px] text-ink-muted">Form and Raw YAML drafts are separate: Save saves the one open. The form keeps the file's comments.</p>
      {raw ? (
        <div className="grid gap-3">
          <label className="grid gap-1">
            repos.yaml
            <Textarea aria-label="Raw repos.yaml" className="min-h-[420px]" value={yaml ?? baseline.yaml} onChange={(e) => setYaml(e.target.value)} spellCheck={false} />
          </label>
          <details className="rounded border border-hairline p-3">
            <summary className="cursor-pointer font-medium">Search settings guide</summary>
            <p className="my-2 text-ink-muted">
              Showing documented keys for format {format}. Format 1 accepts later settings as extra properties; their shape is <strong>not validated by the format 1 schema</strong>. Unknown
              extension keys are project-defined.
            </p>
            <Input aria-label="Search settings guide" placeholder="Search paths and descriptions" value={query} onChange={(e) => setQuery(e.target.value)} />
            <div className="mt-3 grid gap-2">
              {guide.map((f) => (
                <article key={f.path} className="rounded border border-hairline p-2">
                  <strong className={mono}>{f.path}</strong>
                  <p className="my-1">{f.description}</p>
                  {f.default && <small className="text-ink-muted">Default {f.default}. </small>}
                  {format === 1 && f.format === 1 && <small className="text-ink-muted">Supported by current meta-kit; not validated by format 1 schema.</small>}
                </article>
              ))}
            </div>
          </details>
        </div>
      ) : (
        <div className="grid grid-cols-[200px_minmax(0,1fr)] items-start gap-6 phone:grid-cols-1 phone:gap-2.5">
          <nav aria-label="Setting groups" className="sticky top-3 grid gap-0.5 phone:static phone:flex phone:overflow-x-auto phone:border-b phone:border-hairline phone:pb-1.5">
            {GROUPS.map((g) => (
              <a key={g.id} href={`#s-${g.id}`} className="flex flex-none items-center justify-between gap-2 rounded px-2.5 py-[7px] text-[13px] text-ink-body no-underline hover:bg-surface">
                {g.id === 'code' && current ? `${g.name} · ${current}` : g.name}
              </a>
            ))}
          </nav>
          <div key={generation}>
            {GROUPS.map((g) => (
              <section key={g.id} id={`s-${g.id}`} className={SECTION} aria-labelledby={`h-${g.id}`}>
                <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 id={`h-${g.id}`} className="m-0 text-[15px]">
                    {g.id === 'code' && current ? `${g.name} · ${current}` : g.name}
                  </h2>
                  {g.id === 'hosts' && <SettingTip s={setting('hosts')} />}
                  {g.note && <p className="m-0 text-[12.5px] text-ink-muted">{g.note}</p>}
                </div>
                {sections[g.id]}
              </section>
            ))}
          </div>
        </div>
      )}
      {baseline.errors.map((e, i) => (
        <p key={i} role="alert">
          Line {e.line}: {e.message}
        </p>
      ))}
      <div className="sticky bottom-0 z-[5] -mx-1 flex flex-wrap items-center gap-2.5 border-t border-hairline bg-page px-1 py-2.5 phone:bottom-[calc(64px+env(safe-area-inset-bottom))]">
        <span className="mr-auto text-[12px] text-ink-muted phone:hidden">Draft values are local until saved. Changes commit as your linked GitHub account.</span>
        <Button
          onClick={() => {
            resetChanges();
            setMessage('');
            reload();
          }}
          disabled={busy}
        >
          Reload
        </Button>
        <Button variant="primary" disabled={busy || !baseline.can_write || !dirty} onClick={save}>
          Save settings
        </Button>
      </div>
      {message && (
        <p role="status" className="m-0 whitespace-pre-wrap">
          {message}
        </p>
      )}
    </section>
  );
}

/** One field of a sub-card (a component, a host, proof): its label and (i), then the control. */
function SubField({ path, yamlKey, label, children }: { path: string; yamlKey: string; label?: string; children: ReactNode }) {
  const s = setting(path);
  return (
    <>
      <span className="flex items-center gap-1 text-ink-muted">
        {label ?? s.label}
        <SettingTip s={s} yamlKey={yamlKey} label={label} />
      </span>
      <div className="min-w-0">{children}</div>
    </>
  );
}

export function RepoSettingsPage() {
  const { ws: wsParam, repo: repoParam } = useParams();
  const { me } = useSession();
  const ws = Number(wsParam);
  const repo = Number(repoParam);
  const workspace = findWorkspace(me, ws);
  if (!workspace || workspace.role !== 'owner' || !Number.isInteger(repo)) return <NotFoundPage />;
  return (
    <Shell ws={ws}>
      <main className="mx-auto max-w-[1080px] p-5">
        <h1>Repository settings</h1>
        <SettingsEditor key={repo} repo={repo} />
      </main>
    </Shell>
  );
}
